import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Receipt, Loader2 } from "lucide-react";
import { SaveSettingsBar } from "./SaveSettingsBar";
import { invoiceProvider } from "@/services/invoiceGeneration";
import { toast } from "sonner";

/**
 * vat_number/f_skatt/address are #287 additions to `companies` used when issuing
 * invoices. vat_registered/f_skatt are also asked once at onboarding, but this is
 * the only place to correct any of them afterwards — there was previously no UI
 * at all to edit these once onboarding finished.
 */
export const CompanySellerInfoSettings = () => {
  const t = useTranslations('consultantSettings.sellerInfo');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vatRegistered, setVatRegistered] = useState(false);
  const [vatNumber, setVatNumber] = useState('');
  const [fSkatt, setFSkatt] = useState(false);
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const seller = await invoiceProvider.getSeller();
      setVatRegistered(seller.vatRegistered);
      setVatNumber(seller.vatNumber);
      setFSkatt(seller.fSkatt);
      setStreet(seller.addressStreet);
      setPostalCode(seller.addressPostalCode);
      setCity(seller.addressCity);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading company seller info:', error);
      toast.error(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await invoiceProvider.updateSellerProfile({
        vatRegistered, vatNumber, fSkatt,
        addressStreet: street, addressPostalCode: postalCode, addressCity: city,
      });
      setHasChanges(false);
      toast.success(t('saved'));
    } catch (error) {
      console.error('Error saving company seller info:', error);
      toast.error(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const markChanged = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setHasChanges(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          {t('title')}
        </CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>{t('vatRegistered')}</Label>
                <p className="text-xs text-muted-foreground">{t('vatRegisteredHint')}</p>
              </div>
              <Switch checked={vatRegistered} onCheckedChange={markChanged(setVatRegistered)} disabled={saving} />
            </div>

            {vatRegistered && (
              <div className="space-y-2">
                <Label htmlFor="vat-number">{t('vatNumber')}</Label>
                <Input id="vat-number" value={vatNumber} onChange={(e) => markChanged(setVatNumber)(e.target.value)} disabled={saving} />
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border p-3">
              <Label>{t('fSkatt')}</Label>
              <Switch checked={fSkatt} onCheckedChange={markChanged(setFSkatt)} disabled={saving} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address-street">{t('street')}</Label>
              <Input id="address-street" value={street} onChange={(e) => markChanged(setStreet)(e.target.value)} disabled={saving} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="address-postal-code">{t('postalCode')}</Label>
                <Input id="address-postal-code" value={postalCode} onChange={(e) => markChanged(setPostalCode)(e.target.value)} disabled={saving} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address-city">{t('city')}</Label>
                <Input id="address-city" value={city} onChange={(e) => markChanged(setCity)(e.target.value)} disabled={saving} />
              </div>
            </div>
          </>
        )}

        <SaveSettingsBar onSave={handleSave} status={{ loading, saving, hasChanges }} namespace="consultantSettings.sellerInfo" />
      </CardContent>
    </Card>
  );
};
